import React, { useEffect, useState } from "react";
import { ArrowLeft, Play, Tv, Tag } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { showColor, shortShowLabel } from "@/lib/showColors";

// ─── ChannelBrowse ────────────────────────────────────────────────────
// Full-viewport channel landing page — hero backdrop + tape grid.
// Reached from the ChannelGuide's "▶▶ BROWSE" affordance. Click any
// tile to open TapeDetail for that tape. Click "▶ WATCH FROM START" to
// jump straight to tape #1 without opening a detail page.
//
// Artwork cascade per tile (mirrors SpotlightHero / ComingUpNext so a
// consistent library looks visually cohesive top-to-bottom):
//   1. video.backdrop_url
//   2. video.poster_url  (portrait TMDb — still visually pleasant on 16:9)
//   3. channel.thumbnail_url
//   4. kind-default (YT hqdefault / IA services/img)
//   5. phosphor gradient placeholder
export default function ChannelBrowse({
  channel,
  videos,
  isOwner,
  apiBase,
  authHeaders,
  onBack,
  onTune,
  onOpenTape,
  onVideosChanged,
}) {
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTagValue, setBulkTagValue] = useState("");
  const [bulkTagSaving, setBulkTagSaving] = useState(false);
  const [bulkTagScope, setBulkTagScope] = useState("untagged"); // "untagged" | "all"

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onBack?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  if (!channel) return null;
  const list = videos || [];
  const numberLabel = channel.numberLabel || String(channel.number || "").padStart(2, "0");
  const heroBg = channel.thumbnail_url || channel.guide_poster_url || null;

  const artFor = (v) => {
    if (v.backdrop_url) return v.backdrop_url;
    if (v.poster_url) return v.poster_url;
    if (channel.thumbnail_url) return channel.thumbnail_url;
    if (v.kind === "youtube" && v.youtube_id) return `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`;
    if (v.kind === "archive" && v.archive_id) return `https://archive.org/services/img/${v.archive_id}`;
    return null;
  };

  return (
    <div
      className="absolute inset-0 z-40 bg-black text-[var(--phosphor)] overflow-y-auto scrollbar-crt"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      data-testid="channel-browse"
    >
      {/* Hero backdrop */}
      <div className="relative w-full" style={{ height: "clamp(220px, 34vh, 420px)" }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 30% 40%, color-mix(in srgb, var(--phosphor) 22%, transparent) 0%, transparent 60%), linear-gradient(135deg, #050a05, #0a1408)",
          }}
        />
        {heroBg && (
          <img
            src={heroBg}
            alt={channel.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.95) 100%)" }}
        />
        <div className="absolute inset-0 pointer-events-none scanlines opacity-70" />

        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          data-testid="browse-back"
          className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 border border-[var(--phosphor)] bg-black/70 text-[var(--phosphor)] px-3 py-1.5 text-[11px] tracking-[0.3em] hover:bg-[var(--phosphor)] hover:text-black transition-colors"
        >
          <ArrowLeft size={14} />
          BACK TO GUIDE
        </button>

        {/* Hero copy */}
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 pb-6 md:pb-10 max-w-5xl">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] opacity-90 mb-2">
            <Tv size={12} />
            <span>CH {numberLabel}</span>
            <span className="opacity-60">·</span>
            <span>{list.length} TAPE{list.length === 1 ? "" : "S"}</span>
          </div>
          <div
            className="text-3xl md:text-5xl xl:text-6xl font-bold tracking-[0.02em] text-white leading-tight"
            style={{ fontFamily: "VT323, monospace", textShadow: "0 2px 10px rgba(0,0,0,0.9)" }}
            data-testid="browse-channel-name"
          >
            {(channel.name || "CHANNEL").toUpperCase()}
          </div>
          {channel.description && (
            <div
              className="mt-2 text-[12px] md:text-sm text-white/85 leading-snug max-w-2xl line-clamp-3"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.75)" }}
            >
              {channel.description}
            </div>
          )}
          {list.length > 0 && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => onTune?.(0)}
                data-testid="browse-watch-from-start"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--phosphor)] text-black text-xs md:text-sm tracking-[0.25em] font-bold hover:brightness-110 transition-all"
              >
                <Play size={14} fill="currentColor" />
                WATCH FROM START
              </button>
              <span className="text-[10px] tracking-widest opacity-60 hidden md:inline">
                OR PICK A TAPE BELOW
              </span>
              {/* Owner-only bulk show-tag input — the Sky-Q sub-channel
                  derivation is driven by each tape's `show_title`. TMDb
                  apply-show sets it automatically, but for mixed-content
                  channels (e.g. Sonic = Underground + Adventures + X)
                  the operator can select "APPLY TO UNTAGGED" + a show
                  name and stamp every un-grouped tape in one shot. */}
              {isOwner && !channel.isSubChannel && !channel.virtual && (
                <button
                  type="button"
                  onClick={() => setBulkTagOpen((v) => !v)}
                  data-testid="browse-tag-show-toggle"
                  className={`inline-flex items-center gap-1 px-3 py-2 text-[10px] md:text-xs tracking-[0.25em] border transition-colors ${
                    bulkTagOpen
                      ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] text-[var(--phosphor)]"
                      : "border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] text-[color-mix(in_srgb,var(--phosphor)_85%,transparent)] hover:border-[var(--phosphor)] hover:text-[var(--phosphor)]"
                  }`}
                >
                  <Tag size={12} />
                  TAG AS SHOW…
                </button>
              )}
            </div>
          )}
          {isOwner && bulkTagOpen && !channel.isSubChannel && !channel.virtual && (
            <div
              className="mt-3 bg-black/85 border border-[var(--phosphor)] p-3 max-w-2xl"
              style={{ fontFamily: "JetBrains Mono, monospace" }}
              data-testid="browse-tag-panel"
            >
              <div className="text-[10px] tracking-[0.3em] opacity-80 mb-2">
                STAMP EVERY TAPE ON THIS CHANNEL WITH A SHOW/SERIES TITLE
              </div>
              <div className="text-[9px] opacity-70 leading-snug mb-3">
                Sub-channels (05.1 / 05.2 / 05.3) auto-appear in the guide once ≥ 2 distinct show titles exist on this channel. Leave blank to remove the tag from the chosen scope.
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  type="text"
                  value={bulkTagValue}
                  onChange={(e) => setBulkTagValue(e.target.value)}
                  placeholder="e.g. Sonic Underground"
                  data-testid="browse-tag-input"
                  className="flex-1 bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1.5 text-[12px] text-[var(--phosphor)]"
                />
                <select
                  value={bulkTagScope}
                  onChange={(e) => setBulkTagScope(e.target.value)}
                  data-testid="browse-tag-scope"
                  className="bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1.5 text-[11px] text-[var(--phosphor)]"
                >
                  <option value="untagged">SCOPE: UNTAGGED ONLY</option>
                  <option value="all">SCOPE: ALL TAPES (overwrite)</option>
                </select>
                <button
                  type="button"
                  data-testid="browse-tag-apply"
                  disabled={bulkTagSaving}
                  onClick={async () => {
                    const targets = list.filter((v) =>
                      bulkTagScope === "all" ? true : !(v.show_title || "").trim(),
                    );
                    if (targets.length === 0) {
                      toast.info("NOTHING TO TAG", { description: bulkTagScope === "untagged" ? "Every tape on this channel is already tagged." : "This channel has no tapes." });
                      return;
                    }
                    setBulkTagSaving(true);
                    let ok = 0;
                    let fail = 0;
                    // Fire PATCH sequentially so error surfacing is
                    // clear + we don't hammer the backend when a
                    // channel has hundreds of tapes.
                    for (const v of targets) {
                      try {
                        await axios.patch(
                          `${apiBase}/videos/${v.id}`,
                          { show_title: bulkTagValue.trim() },
                          { headers: { "Content-Type": "application/json", ...(authHeaders ? authHeaders() : {}) } },
                        );
                        ok += 1;
                      } catch (e) {
                        fail += 1;
                      }
                    }
                    setBulkTagSaving(false);
                    if (fail === 0) {
                      toast.success("SHOW TAG APPLIED", { description: `${ok} tape${ok === 1 ? "" : "s"} → ${bulkTagValue.trim() || "(cleared)"}` });
                    } else {
                      toast.warning("PARTIAL SAVE", { description: `${ok} ok · ${fail} failed` });
                    }
                    setBulkTagOpen(false);
                    onVideosChanged?.();
                  }}
                  className="border border-[var(--phosphor)] bg-[var(--phosphor)] text-black px-3 py-1.5 text-[11px] tracking-[0.25em] font-bold hover:brightness-110 transition-all disabled:opacity-40"
                >
                  {bulkTagSaving ? "APPLYING…" : "APPLY"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tape grid */}
      <div className="px-6 md:px-12 py-6 md:py-8">
        <div className="text-[11px] tracking-[0.4em] opacity-80 mb-3 phosphor-glow">
          ┌─ ALL TAPES ─┐
        </div>
        {list.length === 0 ? (
          <div className="py-16 text-center text-sm opacity-60 tracking-[0.3em]">
            &gt; NO TAPES ON THIS CHANNEL YET
          </div>
        ) : (
          <div
            className="grid gap-3 md:gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
            data-testid="browse-grid"
          >
            {list.map((v, i) => {
              const art = artFor(v);
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => onOpenTape?.(v.id)}
                  data-testid={`browse-tile-${v.id}`}
                  className="group text-left focus:outline-none ring-1 ring-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] hover:ring-[var(--phosphor)] transition-all bg-black"
                >
                  <div className="relative w-full aspect-video overflow-hidden">
                    {art ? (
                      <img
                        src={art}
                        alt={v.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage:
                            "radial-gradient(ellipse at 40% 40%, color-mix(in srgb, var(--phosphor) 22%, transparent) 0%, transparent 60%), linear-gradient(135deg, #050a05, #0a1408)",
                        }}
                      />
                    )}
                    <div
                      className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
                      style={{ background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.9) 100%)" }}
                    />
                    <div className="absolute inset-0 pointer-events-none scanlines opacity-55" />
                    <div className="absolute top-1.5 left-1.5 text-[10px] tracking-[0.2em] px-1.5 py-0.5 bg-black/80 border border-[var(--phosphor)] text-[var(--phosphor)] font-bold tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="p-2 space-y-0.5">
                    <div className="text-xs md:text-sm text-white font-medium line-clamp-2 leading-tight">
                      {v.title || "UNTITLED"}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] opacity-70 tabular-nums flex-wrap">
                      {v.show_title && (() => {
                        const c = showColor(v.show_title);
                        return (
                          <span
                            className="text-[9px] px-1 py-0.5 tracking-wider border font-medium"
                            style={{ color: c.color, borderColor: c.border, background: c.bg }}
                            title={v.show_title}
                          >
                            {shortShowLabel(v.show_title, 22)}
                          </span>
                        );
                      })()}
                      {v.year && <span>{v.year}</span>}
                      {v.year && v.rt_score != null && <span>·</span>}
                      {v.rt_score != null && <span>{v.rt_score}%</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
